// ABOUTME: Reusable overview stat card: neutral icon tile with a coloured glyph, label and value.
// ABOUTME: Pass an array as value to show a split figure separated by a muted slash.

import { Box, useTheme } from "@mui/material";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { tokens } from "../../../theme";
import Text from "../text/Text";
import Heading from "../text/Heading";
import ShTextSkeleton from "../skeleton/ShTextSkeleton";

// The order overview cards are coloured in, so a row of them looks the same wherever it is
// built. StatCardRow walks this list by position; a card that names its own colour keeps it.
// Longer rows wrap around from the start.
export const STAT_CARD_COLORS = ["lightblue", "green", "orange", "yellow", "purple", "pink", "blue", "red"];

// A filled card takes its surface from the base palette, the one scale that reads as a light
// tint on a light theme and a dark one on a dark theme, which is what the warehouse dashboard
// tiles are built from. Not every pastel hue has an entry there, so a filled card asking for
// one of those is drawn in blue instead.
const FILLED_COLORS = ["primary", "blue", "purple", "green", "orange", "red", "yellow", "pink"];

// Callers must pass the icon with className="logo" so the chip fill styling applies,
// e.g. <StatCard icon={<LocalShippingIcon className="logo" />} ... />.
//
// value takes a single figure or an array of them. An array renders as "a / b", the slash
// muted so the figures themselves stay the thing you read first.
//
// filled colours the whole card instead of the icon chip alone, for a row that is a panel of
// its own rather than a strip above a grid.
const StatCard = ({ label, value, icon, color = "lightblue", trend = null, valueColor, isLoading = false, filled = false }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const isDark = theme.palette.mode === "dark";

    const hasTrend = trend !== null && trend !== undefined;
    const up = hasTrend && trend >= 0;

    // A plain card stays neutral so a row of them reads as one surface, and carries its colour
    // in the icon tile alone: the tile takes the translucent end of the pastel scale, which is a
    // light tint on the white card and a subtle one in dark mode, and the glyph the solid end so
    // it stays legible on top of it.
    //
    // A filled card spreads that colour over the whole surface and steps the tile one shade
    // darker, so the chip still reads as a chip against it.
    const filledColor = FILLED_COLORS.includes(color) ? color : "blue";
    const surface = filled ? colors[filledColor][100] : (isDark ? colors.grey[200] : colors.grey[0]);
    const accent = filled ? colors[filledColor][400] : colors.pastel.light[color][600];
    const accentTint = filled ? colors[filledColor][200] : colors.pastel.st[color][200];
    const values = Array.isArray(value) ? value : [value];

    return (
        <Box
            sx={{
                flex: 1,
                background: surface,
                border: filled ? "none" : `1px solid ${isDark ? colors.grey[300] : colors.grey[100]}`,
                borderRadius: 4,
                padding: 3,
                display: "flex",
                alignItems: "center",
                ".logo-box": {
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    background: accentTint,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    "& .logo": {
                        fill: accent,
                    }
                },
                "@media screen and (max-width: 47.9375em)": {
                    p: 2,
                    ".logo-box": { width: 32, height: 32 }
                }
            }}
        >
            <Box className="logo-box">{icon}</Box>
            <Box sx={{ pl: 2, display: "flex", flexDirection: "column", overflow: "hidden", flex: 1 }}>
                <Text light noBreak>{label}</Text>
                {isLoading ? (
                    <Box sx={{ pt: 1, width: 1, maxWidth: 120 }}>
                        <ShTextSkeleton height={22} color={accentTint} />
                    </Box>
                ) : (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Heading sx={{ pt: 0.5, color: `${valueColor ?? colors.txt["primary"]} !important`, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {values.map((entry, index) => (
                                <Box component="span" key={index}>
                                    {index > 0 && (
                                        <Box component="span" sx={{ px: 0.75, color: colors.txt["secondary"], fontWeight: 400 }}>/</Box>
                                    )}
                                    {entry}
                                </Box>
                            ))}
                        </Heading>
                        {hasTrend && (
                            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", fontSize: 13, fontWeight: 600, color: up ? "#1f9d55" : "#e3342f" }}>
                                {up ? <ArrowDropUpIcon fontSize="small" /> : <ArrowDropDownIcon fontSize="small" />}
                                {Math.abs(trend)}%
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default StatCard;
